<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useArdhisasaAuth } from 'dashboard/composables/useArdhisasaAuth';

import ArdhisasaAuthModal from '../tickets/components/ArdhisasaAuthModal.vue';
import {
  getInvoiceDetail,
  getSerialDocument,
  getParcelStatus,
  getParcelPropertyInfo,
  getLandRegister,
  getRentInvoices,
} from 'dashboard/api/ardhisasaLandRecords';

const { t } = useI18n();

// ── Auth ──────────────────────────────────────────────────────────────────────
const {
  showAuth,
  handleAuthenticated,
  dismissAuth,
  store: authStore,
} = useArdhisasaAuth();

const ardhisasaToken = computed(() => authStore.token || '');
const ardhisasaJwt = computed(() => authStore.jwt || '');

// ── Tab state ─────────────────────────────────────────────────────────────────
const activeTab = ref('documents');

// ── Documents tab ─────────────────────────────────────────────────────────────
const docSearchType = ref('invoice');
const docReference = ref('');
const docLoading = ref(false);
const docResult = ref(null);
const docError = ref('');

const docTypeLabel = type => {
  const map = {
    invoice: t('ARDHISASA.LAND_RECORDS.DOCUMENTS.TYPE_INVOICE'),
    receipt: t('ARDHISASA.LAND_RECORDS.DOCUMENTS.TYPE_RECEIPT'),
    serial: t('ARDHISASA.LAND_RECORDS.DOCUMENTS.TYPE_SERIAL'),
  };
  return map[type] || type;
};

const docPlaceholder = computed(() => {
  if (docSearchType.value === 'serial') return 'Enter Serial Number';
  return `Enter ${docTypeLabel(docSearchType.value)} reference number`;
});

watch(docSearchType, () => {
  docReference.value = '';
  docResult.value = null;
  docError.value = '';
});

const searchDocument = async () => {
  if (!docReference.value.trim()) return;
  docLoading.value = true;
  docResult.value = null;
  docError.value = '';
  try {
    let res;
    if (docSearchType.value === 'serial') {
      res = await getSerialDocument(ardhisasaToken.value, ardhisasaJwt.value, {
        serial_number: docReference.value.trim(),
      });
      if (res.data.details) {
        docResult.value = {
          ...res.data.details,
          document_type: res.data.document_type,
        };
      } else {
        docResult.value = { ...res.data, _flat: true };
      }
    } else if (docSearchType.value === 'receipt') {
      res = await getSerialDocument(ardhisasaToken.value, ardhisasaJwt.value, {
        document_type: 'RECEIPT',
        reference_number: docReference.value.trim(),
      });
      docResult.value = {
        ...res.data.details,
        document_type: res.data.document_type,
      };
    } else {
      res = await getInvoiceDetail(
        ardhisasaToken.value,
        ardhisasaJwt.value,
        docReference.value.trim()
      );
      docResult.value = res.data;
    }
  } catch {
    docError.value = t('ARDHISASA.LAND_RECORDS.DOCUMENTS.EMPTY');
  } finally {
    docLoading.value = false;
  }
};

// ── Parcel tab ────────────────────────────────────────────────────────────────
const parcelNumber = ref('');
const parcelLoading = ref(false);
const parcelError = ref('');
const parcelStatus = ref(null);
const parcelProperty = ref(null);
const parcelRentInvoices = ref([]);
const parcelRegister = ref(null);

const searchParcel = async () => {
  if (!parcelNumber.value.trim()) return;
  parcelLoading.value = true;
  parcelError.value = '';
  parcelStatus.value = null;
  parcelProperty.value = null;
  parcelRentInvoices.value = [];
  parcelRegister.value = null;

  const pn = parcelNumber.value.trim();
  const tok = ardhisasaToken.value;
  const jwt = ardhisasaJwt.value;

  try {
    const statusRes = await getParcelStatus(tok, jwt, pn);
    parcelStatus.value = statusRes.data;
    const tenureType = statusRes.data?.tenure_type || '';

    // Fire in parallel — each section populates as its request resolves
    getParcelPropertyInfo(tok, jwt, pn, tenureType)
      .then(r => {
        parcelProperty.value = r.data;
      })
      .catch(() => {});
    getRentInvoices(tok, jwt, pn)
      .then(r => {
        parcelRentInvoices.value = r.data?.results || [];
      })
      .catch(() => {});
    getLandRegister(tok, jwt, pn, tenureType)
      .then(r => {
        parcelRegister.value = r.data;
      })
      .catch(() => {});
  } catch {
    parcelError.value = t('ARDHISASA.LAND_RECORDS.PARCEL.EMPTY');
  } finally {
    parcelLoading.value = false;
  }
};

// ── Land Rent tab ─────────────────────────────────────────────────────────────
const rentParcelNumber = ref('');
const rentLoading = ref(false);
const rentError = ref('');
const rentResults = ref([]);

const searchRentInvoices = async () => {
  if (!rentParcelNumber.value.trim()) return;
  rentLoading.value = true;
  rentError.value = '';
  rentResults.value = [];
  try {
    const { data } = await getRentInvoices(
      ardhisasaToken.value,
      ardhisasaJwt.value,
      rentParcelNumber.value.trim()
    );
    rentResults.value = data?.results || [];
    if (!rentResults.value.length)
      rentError.value = t('ARDHISASA.LAND_RECORDS.LAND_RENT.NO_RESULTS');
  } catch (e) {
    const msg = e.response?.data?.details || e.response?.data?.message;
    rentError.value =
      typeof msg === 'string'
        ? msg
        : t('ARDHISASA.LAND_RECORDS.LAND_RENT.FETCH_ERROR');
  } finally {
    rentLoading.value = false;
  }
};

// ── Helpers ───────────────────────────────────────────────────────────────────
function toSentenceCase(str) {
  if (!str) return '—';
  return str
    .replace(/_/g, ' ')
    .toLowerCase()
    .replace(/^\w/, c => c.toUpperCase());
}

function formatKES(amount) {
  if (!amount) return '—';
  return `KES ${Number(amount).toLocaleString('en-KE', {
    minimumFractionDigits: 2,
  })}`;
}

function formatDate(str) {
  if (!str) return '—';
  const d = new Date(str);
  if (Number.isNaN(d.getTime())) return str;
  return d.toLocaleDateString('en-KE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

function docStatusStyle(status) {
  const map = {
    PENDING: { background: '#d97706', color: '#fff' },
    COMPLETE: { background: '#16a34a', color: '#fff' },
    CANCELLED: { background: '#dc2626', color: '#fff' },
  };
  return map[status] || { background: '#64748b', color: '#fff' };
}

function boolBadgeStyle(val) {
  return val
    ? { background: '#16a34a', color: '#fff' }
    : { background: '#dc2626', color: '#fff' };
}

function enumerationBadgeStyle(status) {
  return status === 'ENUMERATED'
    ? { background: '#16a34a', color: '#fff' }
    : { background: '#dc2626', color: '#fff' };
}

function entryStatusStyle(status) {
  return status === 'ACTIVE'
    ? { background: '#16a34a', color: '#fff' }
    : { background: '#64748b', color: '#fff' };
}

// Parses "NAME (ID) (PROPRIETOR) (OWNERSHIP_TYPE)" entries from the land register.
// Masks the ID (keep first 6 chars + last char, replace middle with ****).
// Returns a formatted string: "NAME - ID_MASKED - OWNERSHIP_TYPE"
function formatProprietor(raw) {
  const items = Array.isArray(raw) ? raw : [raw];
  return (
    items
      .map(item => {
        if (!item) return null;
        const match = String(item).match(
          /^(.*?)\s*\(([^)]+)\)\s*\(PROPRIETOR\)\s*\(([^)]+)\)$/i
        );
        if (!match) return String(item);
        const [, name, id, ownershipType] = match;
        const maskedId =
          id.length > 7 ? `${id.slice(0, 6)}****${id.slice(-1)}` : id;
        return `${name.trim()} - ${maskedId} - ${ownershipType}`;
      })
      .filter(Boolean)
      .join(', ') || '—'
  );
}
</script>

<template>
  <div class="flex flex-col flex-1 w-full h-full overflow-hidden bg-white">
    <ArdhisasaAuthModal
      v-if="showAuth"
      @authenticated="handleAuthenticated"
      @close="dismissAuth"
    />
    <div class="p-6 border-b border-gray-200 flex-shrink-0">
      <h1 class="text-xl font-semibold text-gray-900">
        {{ t('ARDHISASA.LAND_RECORDS.TITLE') }}
      </h1>
    </div>

    <!-- Tabs -->
    <div class="flex border-b border-gray-200 px-6 flex-shrink-0">
      <button
        class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
        :class="
          activeTab === 'documents'
            ? 'border-[#71481d] text-[#71481d]'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        "
        @click="activeTab = 'documents'"
      >
        {{ t('ARDHISASA.LAND_RECORDS.TAB_DOCUMENTS') }}
      </button>
      <button
        class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
        :class="
          activeTab === 'parcel'
            ? 'border-[#71481d] text-[#71481d]'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        "
        @click="activeTab = 'parcel'"
      >
        {{ t('ARDHISASA.LAND_RECORDS.TAB_PARCEL') }}
      </button>
      <button
        class="px-4 py-3 text-sm font-medium border-b-2 transition-colors"
        :class="
          activeTab === 'land_rent'
            ? 'border-[#71481d] text-[#71481d]'
            : 'border-transparent text-gray-500 hover:text-gray-700'
        "
        @click="activeTab = 'land_rent'"
      >
        {{ t('ARDHISASA.LAND_RECORDS.TAB_LAND_RENT') }}
      </button>
    </div>

    <!-- ── Documents Tab ──────────────────────────────────────────────────── -->
    <div
      v-if="activeTab === 'documents'"
      class="flex-1 overflow-y-auto p-6 space-y-4"
    >
      <!-- Search type pills -->
      <div class="flex gap-2 flex-wrap">
        <button
          v-for="type in ['invoice', 'receipt', 'serial']"
          :key="type"
          class="px-3 py-1 rounded-full text-sm font-medium transition-colors"
          :class="
            docSearchType === type
              ? 'bg-[#71481d] text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          "
          @click="docSearchType = type"
        >
          {{ docTypeLabel(type) }}
        </button>
      </div>

      <!-- Reference input + search -->
      <div class="flex gap-2">
        <input
          v-model="docReference"
          type="text"
          class="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#71481d]"
          :placeholder="docPlaceholder"
          @keyup.enter="searchDocument"
        />
        <button
          class="px-4 py-2 bg-[#71481d] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          :disabled="docLoading"
          @click="searchDocument"
        >
          {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.SEARCH_BUTTON') }}
        </button>
      </div>

      <!-- Loading -->
      <div v-if="docLoading" class="text-sm text-gray-500">
        {{ t('ARDHISASA.LAND_RECORDS.LOADING') }}
      </div>

      <!-- Error -->
      <div v-if="docError" class="text-sm text-red-600">
        {{ docError }}
      </div>

      <!-- Flat serial result (e.g. VALUATION CERTIFICATE) -->
      <div
        v-if="docResult && docResult._flat"
        class="border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-base font-semibold text-gray-900">
              {{ docResult.process_type || docResult.serial_number }}
            </p>
            <p class="text-sm text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.TYPE_SERIAL') }}
            </p>
          </div>
        </div>
        <div class="grid grid-cols-2 gap-4 text-sm">
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.SERIAL_NUMBER') }}
            </p>
            <p class="font-medium">{{ docResult.serial_number || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PARCELS') }}
            </p>
            <p class="font-medium">{{ docResult.parcel_number || '—' }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.DATE_GENERATED') }}
            </p>
            <p>{{ formatDate(docResult.date_generated) }}</p>
          </div>
        </div>
      </div>

      <!-- Result card -->
      <div
        v-if="docResult && !docResult._flat"
        class="border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <!-- Status + header -->
        <div class="flex items-start justify-between gap-3">
          <div>
            <p class="text-base font-semibold text-gray-900">
              {{ docResult.payment_for || docResult.reference_num }}
            </p>
            <p class="text-sm text-gray-500">
              {{ docResult.document_type }}
            </p>
          </div>
          <span
            class="text-xs font-semibold px-2 py-1 rounded-full"
            :style="docStatusStyle(docResult.status)"
          >
            {{ docResult.status }}
          </span>
        </div>

        <!-- Financial grid -->
        <div class="grid grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.AMOUNT') }}
            </p>
            <p class="text-sm font-medium">
              {{ formatKES(docResult.amount) }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.OUTSTANDING') }}
            </p>
            <p class="text-sm font-medium">
              {{ formatKES(docResult.outstanding_amount) }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.BALANCE') }}
            </p>
            <p class="text-sm font-medium">
              {{ formatKES(docResult.balance) }}
            </p>
          </div>
        </div>

        <!-- Dates -->
        <div class="grid grid-cols-3 gap-4">
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.DATE_GENERATED') }}
            </p>
            <p class="text-sm">{{ formatDate(docResult.date_generated) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.DATE_DUE') }}
            </p>
            <p class="text-sm">{{ formatDate(docResult.date_due) }}</p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.DATE_SETTLED') }}
            </p>
            <p class="text-sm">{{ formatDate(docResult.date_settled) }}</p>
          </div>
        </div>

        <!-- Payment channel + parcels -->
        <div class="grid grid-cols-2 gap-4">
          <div v-if="docResult.payment_channel">
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PAYMENT_CHANNEL') }}
            </p>
            <p class="text-sm">
              {{ docResult.payment_channel?.name || docResult.payment_channel }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500">
              {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PARCELS') }}
            </p>
            <p class="text-sm">
              {{
                Array.isArray(docResult.parcel_number)
                  ? docResult.parcel_number.join(', ')
                  : docResult.parcel_number || '—'
              }}
            </p>
          </div>
        </div>

        <!-- Invoice items table -->
        <div v-if="docResult.invoice_items?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.INVOICE_ITEMS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.ITEM_TYPE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.NARRATION') }}
                </th>
                <th
                  class="text-right px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.AMOUNT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(item, idx) in docResult.invoice_items"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ item.invoice_type || item.type }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{ item.narration }}
                </td>
                <td class="px-3 py-2 text-right border border-gray-200">
                  {{ formatKES(item.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Payments made -->
        <div v-if="docResult.payments_made?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PAYMENTS_MADE') }}
          </p>
          <div
            v-for="(pmt, idx) in docResult.payments_made"
            :key="idx"
            class="space-y-3 py-3 border-b border-gray-100 last:border-0"
          >
            <div class="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.AMOUNT') }}
                </p>
                <p class="font-medium">{{ formatKES(pmt.amount) }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PAYMENT_DATE') }}
                </p>
                <p>{{ formatDate(pmt.payment_date) }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.CHANNEL') }}
                </p>
                <p>{{ pmt.payment_channel }}</p>
              </div>
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.RECEIPT_REF') }}
                </p>
                <p>{{ pmt.reference_number }}</p>
              </div>
            </div>
            <!-- payment_information breakdown -->
            <div
              v-if="pmt.payment_information"
              class="bg-gray-50 rounded-lg p-3 grid grid-cols-2 gap-3 text-sm"
            >
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PMT_STATUS') }}
                </p>
                <span
                  class="inline-block text-xs font-semibold px-2 py-0.5 rounded-full"
                  :style="
                    docStatusStyle(
                      pmt.payment_information.status?.toUpperCase()
                    )
                  "
                >
                  {{ pmt.payment_information.status }}
                </span>
              </div>
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.AMOUNT_PAID') }}
                </p>
                <p class="font-medium">
                  {{ formatKES(pmt.payment_information.amount_paid) }}
                </p>
              </div>
              <div>
                <p class="text-xs text-gray-500">
                  {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.PAYMENT_DATE') }}
                </p>
                <p>{{ formatDate(pmt.payment_information.payment_date) }}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty state -->
      <p
        v-if="!docLoading && !docResult && !docError"
        class="text-sm text-gray-400"
      >
        {{ t('ARDHISASA.LAND_RECORDS.DOCUMENTS.EMPTY') }}
      </p>
    </div>

    <!-- ── Parcel Tab ──────────────────────────────────────────────────────── -->
    <div
      v-if="activeTab === 'parcel'"
      class="flex-1 overflow-y-auto p-6 space-y-4"
    >
      <!-- Parcel search -->
      <div class="flex gap-2">
        <input
          v-model="parcelNumber"
          type="text"
          class="flex-1 border border-gray-300 rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#71481d]"
          :placeholder="t('ARDHISASA.LAND_RECORDS.PARCEL.NUMBER_PLACEHOLDER')"
          @keyup.enter="searchParcel"
        />
        <button
          class="px-4 py-2 bg-[#71481d] text-white rounded-lg text-sm font-medium hover:opacity-90 disabled:opacity-50"
          :disabled="parcelLoading"
          @click="searchParcel"
        >
          {{ t('ARDHISASA.LAND_RECORDS.PARCEL.SEARCH_BUTTON') }}
        </button>
      </div>

      <div v-if="parcelLoading" class="text-sm text-gray-500">
        {{ t('ARDHISASA.LAND_RECORDS.LOADING') }}
      </div>
      <div v-if="parcelError" class="text-sm text-red-600">
        {{ parcelError }}
      </div>

      <!-- ── 1. Parcel Status ──────────────────────────────────────────── -->
      <div
        v-if="parcelStatus"
        class="border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <h2 class="text-sm font-semibold text-gray-800">
          {{ t('ARDHISASA.LAND_RECORDS.PARCEL.PARCEL_STATUS') }}
        </h2>

        <!-- Overview table -->
        <table class="w-full text-sm border-collapse">
          <tbody>
            <tr class="border-b border-gray-100">
              <td class="py-2 pr-4 text-xs text-gray-500 w-2/5">
                {{ t('ARDHISASA.LAND_RECORDS.PARCEL.TENURE') }}
              </td>
              <td class="py-2 font-medium">
                {{ parcelStatus.tenure_type || '—' }}
              </td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 pr-4 text-xs text-gray-500">
                {{ t('ARDHISASA.LAND_RECORDS.PARCEL.STATUS') }}
              </td>
              <td class="py-2">
                <span
                  class="text-xs font-semibold px-2 py-0.5 rounded-full"
                  :style="
                    boolBadgeStyle(parcelStatus.parcel_status === 'ACTIVE')
                  "
                >
                  {{ parcelStatus.parcel_status || '—' }}
                </span>
              </td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 pr-4 text-xs text-gray-500">
                {{ t('ARDHISASA.LAND_RECORDS.PARCEL.COUNTY') }}
              </td>
              <td class="py-2">{{ parcelStatus.county || '—' }}</td>
            </tr>
            <tr class="border-b border-gray-100">
              <td class="py-2 pr-4 text-xs text-gray-500">
                {{ t('ARDHISASA.LAND_RECORDS.PARCEL.SUB_COUNTY') }}
              </td>
              <td class="py-2">{{ parcelStatus.sub_county || '—' }}</td>
            </tr>
            <tr>
              <td class="py-2 pr-4 text-xs text-gray-500">
                {{ t('ARDHISASA.LAND_RECORDS.PARCEL.AREA') }}
              </td>
              <td class="py-2">
                {{ parcelStatus.area || '—' }}
                {{ parcelStatus.area_units || '' }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Survey Details -->
        <div>
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.SURVEY_DETAILS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200 w-2/5"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.FIELD') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.VALUE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(label, key) in {
                  land_survey_exists: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.SURVEY_EXISTS'
                  ),
                  land_survey_valid: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.SURVEY_VALID'
                  ),
                  land_survey_active: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.SURVEY_ACTIVE'
                  ),
                }"
                :key="key"
                class="border-b border-gray-100"
              >
                <td
                  class="px-3 py-2 border border-gray-200 text-xs text-gray-500"
                >
                  {{ label }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="boolBadgeStyle(parcelStatus[key])"
                  >
                    {{
                      parcelStatus[key]
                        ? t('ARDHISASA.LAND_RECORDS.PARCEL.YES')
                        : t('ARDHISASA.LAND_RECORDS.PARCEL.NO')
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Admin Details -->
        <div>
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ADMIN_DETAILS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200 w-2/5"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.FIELD') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.VALUE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(label, key) in {
                  land_admin_exists: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.ADMIN_EXISTS'
                  ),
                  land_admin_valid: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.ADMIN_VALID'
                  ),
                  land_admin_active: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.ADMIN_ACTIVE'
                  ),
                }"
                :key="key"
                class="border-b border-gray-100"
              >
                <td
                  class="px-3 py-2 border border-gray-200 text-xs text-gray-500"
                >
                  {{ label }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="boolBadgeStyle(parcelStatus[key])"
                  >
                    {{
                      parcelStatus[key]
                        ? t('ARDHISASA.LAND_RECORDS.PARCEL.YES')
                        : t('ARDHISASA.LAND_RECORDS.PARCEL.NO')
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Registration Details -->
        <div>
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.REGISTRATION_DETAILS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200 w-2/5"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.FIELD') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.VALUE') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(label, key) in {
                  land_registration_exists: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.REGISTRATION_EXISTS'
                  ),
                  land_registration_valid: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.REGISTRATION_VALID'
                  ),
                  land_registration_active: t(
                    'ARDHISASA.LAND_RECORDS.PARCEL.REGISTRATION_ACTIVE'
                  ),
                }"
                :key="key"
                class="border-b border-gray-100"
              >
                <td
                  class="px-3 py-2 border border-gray-200 text-xs text-gray-500"
                >
                  {{ label }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="boolBadgeStyle(parcelStatus[key])"
                  >
                    {{
                      parcelStatus[key]
                        ? t('ARDHISASA.LAND_RECORDS.PARCEL.YES')
                        : t('ARDHISASA.LAND_RECORDS.PARCEL.NO')
                    }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Enumeration Status -->
        <div
          v-if="parcelStatus.land_registration_enumeration_status"
          class="flex items-center gap-3"
        >
          <span class="text-xs text-gray-500">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ENUMERATION') }}
          </span>
          <span
            class="text-xs font-semibold px-2 py-0.5 rounded-full"
            :style="
              enumerationBadgeStyle(
                parcelStatus.land_registration_enumeration_status
              )
            "
          >
            {{ parcelStatus.land_registration_enumeration_status }}
          </span>
        </div>

        <!-- Ownership Details -->
        <div v-if="parcelStatus.ownership_details?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERSHIP_DETAILS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNER_NAME') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERSHIP_RIGHTS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(od, idx) in parcelStatus.ownership_details"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ od.name || '—' }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{ od.ownership_rights || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── 2. Rent Invoices ───────────────────────────────────────────── -->
      <div v-if="parcelRentInvoices.length" class="space-y-3">
        <h2 class="text-sm font-semibold text-gray-800">
          {{ t('ARDHISASA.LAND_RECORDS.PARCEL.RENT_INVOICES') }}
        </h2>
        <div
          v-for="invoice in parcelRentInvoices"
          :key="invoice.id"
          class="bg-white border border-gray-200 rounded-xl p-5 space-y-4"
        >
          <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div>
              <p class="text-xs text-gray-500 mb-0.5">
                {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.PARCEL') }}
              </p>
              <p class="font-medium text-gray-900">
                {{
                  Array.isArray(invoice.parcel_number)
                    ? invoice.parcel_number.join(', ')
                    : invoice.parcel_number
                }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-0.5">
                {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.PAYMENT_FOR') }}
              </p>
              <p class="font-medium text-gray-900">
                {{ invoice.payment_for || '—' }}
              </p>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-0.5">
                {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.STATUS') }}
              </p>
              <span
                class="inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full text-white"
                :style="docStatusStyle(invoice.status)"
              >
                {{ invoice.status }}
              </span>
            </div>
            <div>
              <p class="text-xs text-gray-500 mb-0.5">
                {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.BALANCE') }}
              </p>
              <p class="font-medium text-gray-900">
                {{ formatKES(invoice.balance) }}
              </p>
            </div>
          </div>
          <div
            v-if="invoice.invoice_items?.length"
            class="border-t border-gray-100 pt-4"
          >
            <p
              class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
            >
              {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.INVOICE_ITEMS') }}
            </p>
            <table class="w-full text-sm border-collapse">
              <thead>
                <tr
                  class="text-left text-xs text-gray-500 border-b border-gray-100"
                >
                  <th class="pb-2 pr-4 font-medium">
                    {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.DATE') }}
                  </th>
                  <th class="pb-2 pr-4 font-medium">
                    {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.INVOICE_TYPE') }}
                  </th>
                  <th class="pb-2 pr-4 font-medium">
                    {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.NARRATION') }}
                  </th>
                  <th class="pb-2 font-medium text-right">
                    {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.AMOUNT') }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="item in invoice.invoice_items"
                  :key="item.id"
                  class="border-b border-gray-50 last:border-0"
                >
                  <td class="py-2 pr-4 text-gray-600 whitespace-nowrap">
                    {{ formatDate(item.date_created) }}
                  </td>
                  <td class="py-2 pr-4 text-gray-900">
                    {{ item.invoice_type }}
                  </td>
                  <td class="py-2 pr-4 text-gray-600">{{ item.narration }}</td>
                  <td class="py-2 text-right font-medium text-gray-900">
                    {{ formatKES(item.amount) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- ── 3. Property Info ───────────────────────────────────────────── -->
      <div
        v-if="parcelProperty"
        class="border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <h2 class="text-sm font-semibold text-gray-800">
          {{ t('ARDHISASA.LAND_RECORDS.PARCEL.PROPERTY_INFO') }}
        </h2>

        <!-- Main details table -->
        <table class="w-full text-sm border-collapse">
          <tbody>
            <tr
              v-for="(val, label) in {
                [t('ARDHISASA.LAND_RECORDS.PARCEL.BLOCK_NAME')]:
                  parcelProperty.block_name,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.BLOCK_NUMBER')]:
                  parcelProperty.block_number,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.PARCEL_NUMBER')]:
                  parcelProperty.property_section?.parcel_number,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.NATURE_OF_TITLE')]:
                  parcelProperty.nature_of_title,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.REGISTRATION_SECTION')]:
                  parcelProperty.property_section?.registration_section,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.APPROXIMATE_AREA')]:
                  parcelProperty.property_section?.approximate_area
                    ? `${parcelProperty.property_section.approximate_area} ${parcelProperty.property_section.area_units || ''}`
                    : null,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.LEASE_FROM')]: formatDate(
                  parcelProperty.property_section?.lease_from
                ),
                [t('ARDHISASA.LAND_RECORDS.PARCEL.OPENED')]: formatDate(
                  parcelProperty.property_section?.opened
                ),
                [t('ARDHISASA.LAND_RECORDS.PARCEL.WEF')]: formatDate(
                  parcelProperty.property_section?.wef
                ),
                [t('ARDHISASA.LAND_RECORDS.PARCEL.EDITION')]:
                  parcelProperty.property_section?.edition,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.TERM')]:
                  parcelProperty.property_section?.term,
                [t('ARDHISASA.LAND_RECORDS.PARCEL.RENT')]: parcelProperty
                  .property_section?.rent
                  ? `${formatKES(parcelProperty.property_section.rent)} (${parcelProperty.property_section.rent_currency || ''})`
                  : null,
              }"
              :key="label"
              class="border-b border-gray-100"
            >
              <td class="py-2 pr-4 text-xs text-gray-500 w-2/5">
                {{ label }}
              </td>
              <td class="py-2 font-medium text-gray-800">
                {{ val || '—' }}
              </td>
            </tr>
          </tbody>
        </table>

        <!-- Lessors -->
        <div v-if="parcelProperty.property_section?.lessors?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.LESSORS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <tbody>
              <tr
                v-for="(l, idx) in parcelProperty.property_section.lessors"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{
                    l.digitizing_information?.name ||
                    l.user?.name ||
                    l.user?.names ||
                    '—'
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Lessees -->
        <div v-if="parcelProperty.property_section?.lessees?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.LESSEES') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <tbody>
              <tr
                v-for="(l, idx) in parcelProperty.property_section.lessees"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{
                    l.digitizing_information?.name ||
                    l.user?.name ||
                    l.user?.names ||
                    '—'
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ownership -->
        <div v-if="parcelProperty.ownership?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERSHIP') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.HOLDING_TYPE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.STATUS') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(own, idx) in parcelProperty.ownership"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ toSentenceCase(own.holding_type) }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="boolBadgeStyle(own.active)"
                  >
                    {{
                      own.active
                        ? t('ARDHISASA.LAND_RECORDS.PARCEL.ACTIVE')
                        : t('ARDHISASA.LAND_RECORDS.PARCEL.REVOKED')
                    }}
                  </span>
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{
                    own.owners
                      .map(o => o.user?.name || o.user?.names || '—')
                      .join(', ')
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- ── 4. Land Register ───────────────────────────────────────────── -->
      <div
        v-if="parcelRegister"
        class="border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <h2 class="text-sm font-semibold text-gray-800">
          {{ t('ARDHISASA.LAND_RECORDS.PARCEL.LAND_REGISTER') }}
        </h2>

        <!-- Proprietorship Section -->
        <div v-if="parcelRegister.proprietorship_section?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.PROPRIETORSHIP') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ENTRY_NUMBER') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.DATE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.PROPRIETOR') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.STATUS') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.PARTICULARS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="entry in parcelRegister.proprietorship_section"
                :key="entry.id"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ entry.entry_number }}
                </td>
                <td class="px-3 py-2 border border-gray-200 whitespace-nowrap">
                  {{ formatDate(entry.date_created) }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{ formatProprietor(entry.name_of_registered_proprietor) }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="entryStatusStyle(entry.status)"
                  >
                    {{ entry.status }}
                  </span>
                </td>
                <td
                  class="px-3 py-2 border border-gray-200 text-xs text-gray-600"
                >
                  {{ entry.particulars || '—' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Encumbrance Section -->
        <div v-if="parcelRegister.encumbrance_section?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ENCUMBRANCE') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ENTRY_NUMBER') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.DATE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.ENCUMBRANCE_TYPE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.FURTHER_PARTICULARS') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.STATUS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="enc in parcelRegister.encumbrance_section"
                :key="enc.id"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ enc.entry_number }}
                </td>
                <td class="px-3 py-2 border border-gray-200 whitespace-nowrap">
                  {{ formatDate(enc.date_created) }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{ toSentenceCase(enc.nature_of_encumbrance) }}
                </td>
                <td
                  class="px-3 py-2 border border-gray-200 text-xs text-gray-600"
                >
                  {{ enc.further_particulars || '—' }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="entryStatusStyle(enc.status)"
                  >
                    {{ enc.status }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Ownership from Land Register -->
        <div v-if="parcelRegister.ownership?.length">
          <p class="text-xs font-semibold text-gray-700 mb-2">
            {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERSHIP') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr class="bg-gray-50">
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.HOLDING_TYPE') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.STATUS') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERS') }}
                </th>
                <th
                  class="text-left px-3 py-2 text-xs text-gray-500 border border-gray-200"
                >
                  {{ t('ARDHISASA.LAND_RECORDS.PARCEL.OWNERSHIP_RIGHTS') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(own, idx) in parcelRegister.ownership"
                :key="idx"
                class="border-b border-gray-100"
              >
                <td class="px-3 py-2 border border-gray-200">
                  {{ toSentenceCase(own.holding_type) }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  <span
                    class="text-xs font-semibold px-2 py-0.5 rounded-full"
                    :style="boolBadgeStyle(own.active)"
                  >
                    {{
                      own.active
                        ? t('ARDHISASA.LAND_RECORDS.PARCEL.ACTIVE')
                        : t('ARDHISASA.LAND_RECORDS.PARCEL.REVOKED')
                    }}
                  </span>
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{
                    own.owners
                      .map(o => o.user?.name || o.user?.names || '—')
                      .join(', ')
                  }}
                </td>
                <td class="px-3 py-2 border border-gray-200">
                  {{
                    own.owners.map(o => o.ownership_rights || '—').join(', ')
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty state -->
      <p
        v-if="!parcelLoading && !parcelStatus && !parcelError"
        class="text-sm text-gray-400"
      >
        {{ t('ARDHISASA.LAND_RECORDS.PARCEL.EMPTY') }}
      </p>
    </div>

    <!-- ── Land Rent Tab ──────────────────────────────────────────────────── -->
    <div
      v-if="activeTab === 'land_rent'"
      class="flex-1 overflow-y-auto p-6 space-y-4"
    >
      <!-- Search -->
      <div class="flex gap-2 items-end">
        <div class="flex flex-col gap-1 flex-1">
          <label class="text-xs font-medium text-gray-600">
            {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.NUMBER_LABEL') }}
          </label>
          <input
            v-model="rentParcelNumber"
            type="text"
            :placeholder="
              t('ARDHISASA.LAND_RECORDS.LAND_RENT.NUMBER_PLACEHOLDER')
            "
            class="px-3 py-2 text-sm rounded-lg border border-gray-200 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#71481d]"
            @keyup.enter="searchRentInvoices"
          />
        </div>
        <button
          class="flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#71481d] text-white text-sm font-medium hover:bg-[#5a3915] disabled:opacity-60"
          :disabled="rentLoading"
          @click="searchRentInvoices"
        >
          <span
            v-if="rentLoading"
            class="i-lucide-loader-circle size-4 animate-spin"
          />
          <span v-else class="i-lucide-search size-4" />
          {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.SEARCH_BUTTON') }}
        </button>
      </div>

      <!-- Error -->
      <p v-if="rentError" class="text-sm text-red-600">{{ rentError }}</p>

      <!-- Results -->
      <div
        v-for="invoice in rentResults"
        :key="invoice.id"
        class="bg-white border border-gray-200 rounded-xl p-5 space-y-4"
      >
        <!-- Summary row -->
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
          <div>
            <p class="text-xs text-gray-500 mb-0.5">
              {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.PARCEL') }}
            </p>
            <p class="font-medium text-gray-900">
              {{
                Array.isArray(invoice.parcel_number)
                  ? invoice.parcel_number.join(', ')
                  : invoice.parcel_number
              }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-0.5">
              {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.PAYMENT_FOR') }}
            </p>
            <p class="font-medium text-gray-900">
              {{ invoice.payment_for || '—' }}
            </p>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-0.5">
              {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.STATUS') }}
            </p>
            <span
              class="inline-block text-xs font-semibold px-2.5 py-0.5 rounded-full text-white"
              :style="docStatusStyle(invoice.status)"
            >
              {{ invoice.status }}
            </span>
          </div>
          <div>
            <p class="text-xs text-gray-500 mb-0.5">
              {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.BALANCE') }}
            </p>
            <p class="font-medium text-gray-900">
              {{ formatKES(invoice.balance) }}
            </p>
          </div>
        </div>

        <!-- Invoice items -->
        <div
          v-if="invoice.invoice_items?.length"
          class="border-t border-gray-100 pt-4"
        >
          <p
            class="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2"
          >
            {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.INVOICE_ITEMS') }}
          </p>
          <table class="w-full text-sm border-collapse">
            <thead>
              <tr
                class="text-left text-xs text-gray-500 border-b border-gray-100"
              >
                <th class="pb-2 pr-4 font-medium">
                  {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.DATE') }}
                </th>
                <th class="pb-2 pr-4 font-medium">
                  {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.INVOICE_TYPE') }}
                </th>
                <th class="pb-2 pr-4 font-medium">
                  {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.NARRATION') }}
                </th>
                <th class="pb-2 font-medium text-right">
                  {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.AMOUNT') }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in invoice.invoice_items"
                :key="item.id"
                class="border-b border-gray-50 last:border-0"
              >
                <td class="py-2 pr-4 text-gray-600 whitespace-nowrap">
                  {{ formatDate(item.date_created) }}
                </td>
                <td class="py-2 pr-4 text-gray-900">{{ item.invoice_type }}</td>
                <td class="py-2 pr-4 text-gray-600">{{ item.narration }}</td>
                <td class="py-2 text-right font-medium text-gray-900">
                  {{ formatKES(item.amount) }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Empty state -->
      <p
        v-if="!rentLoading && !rentResults.length && !rentError"
        class="text-sm text-gray-400"
      >
        {{ t('ARDHISASA.LAND_RECORDS.LAND_RENT.EMPTY') }}
      </p>
    </div>
  </div>
</template>
